import React from "react";

// User-facing explanations for the chat-message trigger types (issue #15).
// Keep in sync with docs/user-guide/triggers.md and the matcher in
// services/backend/src/modules/bot/bot-trigger.service.ts.
export interface TriggerTypeHelpEntry {
  description: string;
  examples: { config: string; matches: string; ignores?: string }[];
}

export const TRIGGER_TYPE_HELP: Record<string, TriggerTypeHelpEntry> = {
  keyword: {
    description:
      'Replies when a chat message contains any of your keywords. With Exact Match on, the keyword must stand alone ("sing" won\'t fire on "singing").',
    examples: [
      {
        config: "Keywords: !discord, !social",
        matches: "!discord",
        ignores: "what's the discord?",
      },
      {
        config: "Keywords: brb (Exact Match)",
        matches: "brb, grabbing water",
        ignores: "brbb",
      },
    ],
  },
  pattern: {
    description:
      "Replies when a chat message matches a regular expression. Not case-sensitive by default. Use ^ and $ to match the whole message.",
    examples: [
      {
        config: "Pattern: ^!(help|commands)$",
        matches: "!help",
        ignores: "!help me please",
      },
      {
        config: "Pattern: ^!?lurk(\\s.*)?$",
        matches: "!lurk going to bed",
      },
    ],
  },
  parameterized_keyword: {
    description:
      "A chat command that takes an argument. Fires when a message starts with the command word; everything after it becomes {{parameter}}, which you can use in the reply or the webhook URL.",
    examples: [
      {
        config: "Command: !sr — Reply: Queued {{parameter}}!",
        matches: "!sr Never Gonna Give You Up",
        ignores: "!srsly",
      },
      {
        config: "Command: !so — Reply: Go follow {{parameter}}!",
        matches: "!so @streamer",
      },
    ],
  },
};

export const TriggerTypeHelp: React.FC<{ triggerType: string }> = ({
  triggerType,
}) => {
  const help = TRIGGER_TYPE_HELP[triggerType];
  if (!help) return null;

  return (
    <div
      data-testid="trigger-type-help"
      style={{
        padding: "12px",
        backgroundColor: "#1e293b",
        border: "1px solid #334155",
        borderRadius: "6px",
        fontSize: "13px",
        color: "#cbd5e0",
        marginBottom: "16px",
      }}
    >
      <div style={{ marginBottom: "8px" }}>{help.description}</div>
      <div style={{ color: "#a0aec0", fontSize: "12px" }}>Examples:</div>
      <ul style={{ margin: "4px 0 0", paddingLeft: "18px" }}>
        {help.examples.map((ex) => (
          <li key={ex.config} style={{ marginBottom: "4px" }}>
            <code style={{ color: "#e2e8f0" }}>{ex.config}</code>
            <br />
            <span style={{ color: "#68d391" }}>fires on</span>{" "}
            <code>{ex.matches}</code>
            {ex.ignores && (
              <>
                {" · "}
                <span style={{ color: "#fc8181" }}>not on</span>{" "}
                <code>{ex.ignores}</code>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default TriggerTypeHelp;
